import { Component, OnInit, signal, computed, inject, ChangeDetectionStrategy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LicenseDataService } from '../../core/services/license-data.service';
import { AuthService } from '../../core/services/auth.service';
import { InstitutionalSettingsComponent } from '../institutional-settings/institutional-settings.component';
import { LemonProduct, LemonVariant, LemonSetupResponse, UserSubscription, LicenseGetResponse, InstitutionalLicense } from '../../shared/models/license.models';
import '../../shared/types/lemon-squeezy.types';
import { interval, throwError } from 'rxjs';
import { map, filter, take, timeout, catchError } from 'rxjs/operators';

/**
 * Plans Component
 *
 * Displays available subscription plans from Lemon Squeezy
 * Matches readonly license app interface with tabs for Personal plans and Institutional settings
 */
@Component({
  selector: 'app-plans',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    InstitutionalSettingsComponent
  ],
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlansComponent implements OnInit {
  licenseDataService = inject(LicenseDataService);
  authService = inject(AuthService);

  // Tab management
  selectedTabIndex = signal(0); // 0 = Personal Plans, 1 = Institutional Settings
  selectedBillingIndex = signal(0); // 0 = month, 1 = year

  // Setup data from Lemon Squeezy
  setupData = signal<LemonSetupResponse | null>(null);

  // Track if license data has been loaded (to avoid showing wrong plan initially)
  licenseDataLoaded = signal(false);

  // User's current license tier (from license_get cloud function)
  currentLicenseTier = signal<string>('free');

  // License data from cloud function
  userLicenseData = signal<LicenseGetResponse | null>(null);
  
  // Institutional licenses exposed for child component
  institutionalLicenses = computed<InstitutionalLicense[]>(() => {
    const data = this.userLicenseData();
    return data?.institutionalLicenses?.licenses || [];
  });

  // Registered emails for child component
  registeredEmails = computed(() => {
    const data = this.userLicenseData();
    return data?.registeredEmails || [];
  });

  // Computed values
  currentBillingCycle = computed(() => this.selectedBillingIndex() === 0 ? 'month' : 'year');

  // Get available plans from setup data
  availablePlans = computed(() => {
    const data = this.setupData();
    if (!data) return [];
    return data.products;
  });

  /**
   * Get institutional licenses that provide a specific plan tier
   */
  getInstitutionalLicensesForPlan(planName: string): InstitutionalLicense[] {
    const licenses = this.institutionalLicenses();
    if (!licenses || licenses.length === 0) return [];

    const targetTier = planName.toLowerCase();
    
    return licenses.filter(inst => {
      const instTier = inst.licenseData?.tier?.toLowerCase();
      if (!instTier) return false;

      // Match 'professional' or 'pro'
      if (targetTier.includes('professional') || targetTier.includes('pro')) {
        return instTier.includes('professional') || instTier.includes('pro');
      }
      
      // Match 'essential' or 'basic'
      if (targetTier.includes('essential') || targetTier.includes('basic')) {
        return instTier.includes('essential') || instTier.includes('basic');
      }

      return instTier === targetTier;
    });
  }

  // Get first available plan (Essential)
  firstPlan = computed((): LemonProduct | null => {
    const plans = this.availablePlans();
    return plans.length > 0 ? plans[0] : null;
  });

  // Get second available plan (Professional)
  secondPlan = computed((): LemonProduct | null => {
    const plans = this.availablePlans();
    return plans.length > 1 ? plans[1] : null;
  });

  // Dynamic plan names
  firstPlanName = computed(() => {
    const plan = this.firstPlan();
    return plan?.attributes.name || 'Essential';
  });

  secondPlanName = computed(() => {
    const plan = this.secondPlan();
    return plan?.attributes.name || 'Professional';
  });

  // Dynamic price displays
  firstPlanPriceDisplay = computed(() => {
    const plan = this.firstPlan();
    if (!plan) return null;
    const price = this.getCurrentPrice(plan);
    return price !== null ? price.toFixed(2) : null;
  });

  secondPlanPriceDisplay = computed(() => {
    const plan = this.secondPlan();
    if (!plan) return null;
    const price = this.getCurrentPrice(plan);
    return price !== null ? price.toFixed(2) : null;
  });

  // Computed status for plans to avoid function calls in template
  isFreePlanActive = computed(() => {
    const tier = this.currentLicenseTier().toLowerCase();
    return tier === 'free' || !this.authService.isAuthenticated();
  });

  isFirstPlanCurrent = computed(() => {
    const plan = this.firstPlan();
    return plan ? this.checkIsCurrentPlan(plan) : false;
  });

  isSecondPlanCurrent = computed(() => {
    const plan = this.secondPlan();
    return plan ? this.checkIsCurrentPlan(plan) : false;
  });

  showSubscribeFirstPlan = computed(() => {
    const plan = this.firstPlan();
    return plan ? this.checkShouldShowSubscribeButton(plan) : false;
  });

  showSubscribeSecondPlan = computed(() => {
    const plan = this.secondPlan();
    return plan ? this.checkShouldShowSubscribeButton(plan) : false;
  });

  // Track previous auth state to detect login events
  private previousAuthState = signal<boolean | null>(null);

  constructor() {
    // Setup effect to reload license data when user logs in
    effect(() => {
      const isAuth = this.authService.isAuthenticated();
      const wasAuth = this.previousAuthState();
      
      // On first run, just initialize the state without triggering actions
      if (wasAuth === null) {
        console.log('🔄 [PLANS] Initializing auth state tracking:', isAuth ? 'authenticated' : 'not authenticated');
        this.previousAuthState.set(isAuth);
        return;
      }
      
      // Detect when user transitions from not authenticated to authenticated (manual login)
      if (isAuth && !wasAuth) {
        console.log('🔐 [PLANS] User just logged in, reloading license data...');
        this.loadUserLicense();
      }
      
      // Detect when user logs out
      if (!isAuth && wasAuth) {
        console.log('🚪 [PLANS] User just logged out, resetting license data...');
        this.currentLicenseTier.set('free');
        this.userLicenseData.set(null);
        this.licenseDataLoaded.set(false);
      }
      
      // Update previous state
      this.previousAuthState.set(isAuth);
    });
  }

  async ngOnInit(): Promise<void> {
    console.log('🚀 [PLANS] Component initializing...');
    
    // Load pricing data
    await this.loadPlans();
    
    // Load user's license data
    await this.loadUserLicense();
    
    // Preload the Lemon Squeezy library on initialization
    try {
      await this.ensureLemonSqueezyLoaded();
      this.initializeLemonSqueezy();
    } catch (error) {
      console.warn('⚠️ [PLANS] Lemon Squeezy library not available during init. Will retry when opening checkout.');
    }
    
    console.log('✅ [PLANS] Component initialization complete');
  }

  /**
   * Load user's license data using Firebase Functions (not MemOwl library)
   * SIMPLIFIED: Use existing LicenseDataService for proper Angular/Firebase integration
   */
  async loadUserLicense(): Promise<void> {
    console.log('📡 [PLANS] loadUserLicense() called');
    
    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      console.log('⏭️ [PLANS] User not authenticated, defaulting to free tier');
      this.licenseDataLoaded.set(true);
      this.currentLicenseTier.set('free');
      return;
    }

    try {
      console.log('✅ [PLANS] User authenticated, fetching license data...');
      
      // Use license_get (as requested by user to have all data including institutional)
      const licenseResponse = await this.licenseDataService.getLicense();
      
      console.log('📧 [PLANS] License response registeredEmails:', licenseResponse?.registeredEmails);
      console.log('📧 [PLANS] License response institutionalLicenses:', licenseResponse?.institutionalLicenses);
      
      // Store the full response
      this.userLicenseData.set(licenseResponse);

      // Extract the tier from license data
      let tier = 'free';
      
      if (licenseResponse.license?.tier) {
        tier = licenseResponse.license.tier.toLowerCase();
        console.log('✅ [PLANS] Found license tier from response:', tier);
      } else {
        console.log('⏭️ [PLANS] No explicit license tier found, defaulting to free');
      }

      console.log('🎯 [PLANS] FINAL EXTRACTED TIER:', tier);

      this.currentLicenseTier.set(tier);
      this.licenseDataLoaded.set(true);

      // Now check which plan matches
      const plans = this.availablePlans();

      const matchingPlan = plans.find(plan => this.checkIsCurrentPlan(plan));
      if (matchingPlan) {
        console.log('✅ [PLANS] MATCHING PLAN FOUND:', matchingPlan.attributes.name);
      } else {
        // Only warn if we have a tier but no matching plan
        if (tier !== 'free') {
           console.warn('⚠️ [PLANS] NO MATCHING PLAN FOUND for tier:', tier);
        }
      }

    } catch (error) {
      console.error('❌ [PLANS] Failed to load license data:', error);
      this.licenseDataLoaded.set(true);
      this.currentLicenseTier.set('free');
    }
  }

  async loadPlans(refresh: boolean = false): Promise<void> {
    try {
      const data = await this.licenseDataService.getLemonSetup(refresh);
      this.setupData.set(data);
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  }

  /**
   * Get current price based on billing cycle
   */
  getCurrentPrice(plan: LemonProduct): number | null {
    const data = this.setupData();
    if (!data) return null;

    const variants = data.variants[plan.id] || [];
    const billingCycle = this.currentBillingCycle();

    // Find the variant that matches the billing cycle
    const variant = variants.find((v: LemonVariant) =>
      v.attributes.interval === billingCycle &&
      v.attributes.interval_count === 1
    );

    if (variant && variant.attributes.price) {
      return variant.attributes.price / 100; // Convert from cents to dollars
    }

    return null;
  }

  /**
   * Set billing cycle (month or year)
   */
  setBillingCycle(cycle: 'month' | 'year'): void {
    this.selectedBillingIndex.set(cycle === 'month' ? 0 : 1);
  }

  /**
   * Switch between personal and institutional plans tabs
   */
  switchTab(tab: string): void {
    if (tab === 'personal') {
      this.selectedTabIndex.set(0);
      console.log('📋 [PLANS] Switched to Personal Plans tab');
    } else if (tab === 'settings') {
      this.selectedTabIndex.set(1);
      console.log('⚙️ [PLANS] Switched to Institutional Settings tab');
    }
  }

  /**
   * Select a plan and open checkout
   * Only called when user is authenticated (button is disabled otherwise)
   */
  selectPlan(plan: LemonProduct): void {
    // Button should be disabled if not authenticated, but double-check
    if (!this.authService.isAuthenticated()) {
      console.log('🔒 [PLANS] User not authenticated - button should be disabled');
      return;
    }

    // Check if user already has this plan
    if (this.isCurrentPlan(plan)) {
      console.log('ℹ️ [PLANS] User already has this plan:', plan.attributes.name);
      return;
    }

    const data = this.setupData();
    if (!data) {
      console.error('❌ [PLANS] No setup data available');
      return;
    }

    const variants = data.variants[plan.id] || [];
    const billingCycle = this.currentBillingCycle();

    // Find the variant that matches the billing cycle
    const variant = variants.find((v: LemonVariant) =>
      v.attributes.interval === billingCycle &&
      v.attributes.interval_count === 1
    );

    if (!variant) {
      console.error(`❌ [PLANS] No ${billingCycle}ly variant available for plan: ${plan.attributes.name}`);
      alert(`${billingCycle === 'month' ? 'Monthly' : 'Yearly'} billing is not available for this plan.`);
      return;
    }

    console.log(`🍋 [PLANS] Selecting ${plan.attributes.name} (${billingCycle}ly)`, {
      variantId: variant.id,
      price: variant.attributes.price
    });

    this.openCheckout(variant);
  }

  /**
   * Ensure Lemon Squeezy library is loaded using RxJS observables
   * This method waits for the script to be available before proceeding
   */
  private async ensureLemonSqueezyLoaded(): Promise<void> {
    // Check if already loaded
    if (typeof window !== 'undefined' && typeof window.createLemonSqueezy === 'function') {
      console.log('✅ [PLANS] Lemon Squeezy library already loaded');
      return Promise.resolve();
    }

    console.log('⏳ [PLANS] Waiting for Lemon Squeezy library to load...');

    // Use RxJS interval to check for library availability
    return interval(100).pipe(
      map(() => typeof window !== 'undefined' && typeof window.createLemonSqueezy === 'function' ? window.createLemonSqueezy : null),
      filter(createLemonSqueezy => !!createLemonSqueezy),
      take(1),
      timeout(10000), // 10 second timeout
      catchError(() => {
        console.error('❌ [PLANS] Lemon Squeezy library failed to load after 10 seconds');
        return throwError(() => new Error('Lemon Squeezy library failed to load after 10 seconds'));
      })
    ).toPromise().then(() => {
      console.log('✅ [PLANS] Lemon Squeezy library loaded successfully');
    });
  }

  /**
   * Initialize Lemon Squeezy overlay system
   */
  private initializeLemonSqueezy(): void {
    if (typeof window !== 'undefined' && typeof window.createLemonSqueezy === 'function') {
      window.createLemonSqueezy();
      console.log('✅ [PLANS] Lemon Squeezy overlay system initialized');
    } else {
      console.warn('⚠️ [PLANS] Lemon Squeezy script not yet loaded');
    }
  }

  /**
   * Open Lemon Squeezy checkout in overlay
   * Now ensures the script is loaded and constructs proper checkout URL
   */
  async openCheckout(variant: LemonVariant): Promise<void> {
    // ⭐ CRITICAL: Construct checkout URL using variant slug (matches readonly implementation)
    const variantSlug = variant.attributes.slug;
    if (!variantSlug) {
      console.error('❌ [PLANS] No variant slug available for checkout');
      return;
    }

    // Construct the proper Lemon Squeezy checkout URL
    const baseCheckoutUrl = `https://memowl.lemonsqueezy.com/checkout/buy/${variantSlug}`;
    console.log('🔗 [PLANS] Constructed checkout URL using variant slug:', baseCheckoutUrl);
    console.log('🔍 [PLANS] Variant slug:', variantSlug);

    // Add query parameters for user information and custom data
    const params = new URLSearchParams();
    const user = this.authService.currentUser();

    if (user) {
      // Add user email if available
      if (user.email) {
        params.set('checkout[email]', user.email);
      }

      // Add user display name if available
      if (user.displayName) {
        params.set('checkout[name]', user.displayName);
      }

      // Add custom tracking data
      params.set('checkout[custom][userId]', user.uid || '');
      params.set('checkout[custom][variantId]', variant.id);
      params.set('checkout[custom][variantName]', variant.attributes.name);
    }

    // Add test mode parameter (you can make this configurable)
    // params.set('test', '1'); // Uncomment for test mode

    // Construct final URL with query parameters
    const checkoutUrl = params.toString() ? `${baseCheckoutUrl}?${params.toString()}` : baseCheckoutUrl;
    console.log('🍋 [PLANS] Opening checkout overlay with URL:', checkoutUrl);

    try {
      // ⭐ CRITICAL: Wait for Lemon Squeezy script to load before using it
      await this.ensureLemonSqueezyLoaded();
      
      // Initialize the overlay system
      this.initializeLemonSqueezy();

      // Use Lemon Squeezy overlay if available, otherwise fallback to popup
      if (typeof window !== 'undefined' && window.LemonSqueezy) {
        try {
          window.LemonSqueezy.Url.Open(checkoutUrl);
          console.log('✅ [PLANS] Checkout overlay opened successfully');
        } catch (error) {
          console.warn('⚠️ [PLANS] Overlay failed, falling back to popup:', error);
          this.openCheckoutPopup(checkoutUrl);
        }
      } else {
        console.warn('⚠️ [PLANS] Overlay system not available, using popup fallback');
        this.openCheckoutPopup(checkoutUrl);
      }
    } catch (error) {
      console.error('❌ [PLANS] Failed to load Lemon Squeezy library:', error);
      console.warn('⚠️ [PLANS] Falling back to popup due to library loading failure');
      this.openCheckoutPopup(checkoutUrl);
    }
  }

  /**
   * Fallback: Open checkout in popup window
   */
  private openCheckoutPopup(checkoutUrl: string): void {
    try {
      const popup = window.open(
        checkoutUrl,
        'lemonsqueezy-checkout',
        'width=800,height=600,scrollbars=yes,resizable=yes,status=no,toolbar=no,menubar=no'
      );

      if (!popup) {
        console.error('❌ [PLANS] Popup blocked. Please allow popups for this site.');
        alert('Popup blocked. Please allow popups for this site to complete checkout.');
      } else {
        console.log('✅ [PLANS] Checkout popup opened successfully');
      }
    } catch (error) {
      console.error('❌ [PLANS] Failed to open checkout popup:', error);
    }
  }

  /**
   * Check if this is the user's current plan
   * Compares plan name/slug/id with the user's license tier
   * Internal helper for computed signals
   */
  private checkIsCurrentPlan(plan: LemonProduct): boolean {
    if (!this.licenseDataLoaded()) {
      return false;
    }

    const currentTier = this.currentLicenseTier().toLowerCase();
    const planId = plan.id.toLowerCase();
    const planName = plan.attributes.name.toLowerCase();
    const planSlug = plan.attributes.slug?.toLowerCase() || '';

    // Normalize plan name by removing "memowl" prefix
    const normalizedPlanName = planName.replace(/^memowl\s+/i, '').trim();
    
    // Check multiple matching strategies
    const matchById = currentTier === planId;
    const matchByName = currentTier === planName;
    const matchByNormalized = currentTier === normalizedPlanName;
    const matchBySlug = !!planSlug && currentTier === planSlug;
    
    return matchById || matchByName || matchByNormalized || matchBySlug;
  }

  /**
   * Public method for compatibility if needed, but prefer computed signals
   */
  isCurrentPlan(plan: LemonProduct): boolean {
    return this.checkIsCurrentPlan(plan);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  /**
   * Check if we should show current plan indicators
   * Only show after license data is loaded and user is authenticated
   */
  shouldShowCurrentPlanIndicators(): boolean {
    return this.licenseDataLoaded() && this.authService.isAuthenticated();
  }

  /**
   * Check if free plan is current (no active subscription)
   * Deprecated: Use isFreePlanActive computed signal instead
   */
  isFreePlanCurrent(): boolean {
    return this.isFreePlanActive();
  }

  /**
   * Internal helper for computed signals
   */
  private checkShouldShowSubscribeButton(plan: LemonProduct): boolean {
    // Always show for non-authenticated users
    if (!this.authService.isAuthenticated()) {
      return true;
    }

    // Show while loading license data
    if (!this.licenseDataLoaded()) {
      return true;
    }

    // Don't show if this is the current plan
    if (this.checkIsCurrentPlan(plan)) {
      return false;
    }

    // Check if this plan is higher priority than current tier
    const currentTier = this.currentLicenseTier().toLowerCase();
    const planName = plan.attributes.name.toLowerCase().replace(/^memowl\s+/i, '').trim();

    // Define tier hierarchy (free < essential < professional)
    const tierPriority: { [key: string]: number } = {
      'free': 0,
      'essential': 1,
      'professional': 2,
      'pro': 2
    };

    const currentPriority = tierPriority[currentTier] || 0;
    const planPriority = tierPriority[planName] || 0;

    // Show upgrade button only if plan is higher tier
    return planPriority > currentPriority;
  }

  /**
   * Check if the subscribe/upgrade button should be shown for a plan
   * Shows button only if:
   * 1. User is not authenticated (shows "SIGN IN REQUIRED"), OR
   * 2. Plan is higher tier than current subscription
   */
  shouldShowSubscribeButton(plan: LemonProduct): boolean {
    return this.checkShouldShowSubscribeButton(plan);
  }

  /**
   * Get the highest tier priority from user's subscriptions
   * Note: Currently not used in template, but kept for reference
   */
  get HighestTierPriority(): number {
    const data = this.userLicenseData();
    if (!data || !data.lemonSubscriptions || data.lemonSubscriptions.activeCount === 0) {
      return 0; // free
    }

    // We can calculate priority based on current tier if needed
    const tier = this.currentLicenseTier().toLowerCase();
    const tierPriority: { [key: string]: number } = {
      'free': 0,
      'essential': 1,
      'professional': 2,
      'pro': 2
    };

    return tierPriority[tier] || 0;
  }
}
